import React from "react";
import { cn } from "@/lib/utils";
import { Calculator, ArrowRight } from "lucide-react";

interface IncomeValueCalculatorProps {
  monthlyIncome?: number;
  age?: number;
  retirementAge?: number;
  animated?: boolean;
  className?: string;
}

export function IncomeValueCalculator({
  monthlyIncome = 3000,
  age = 35,
  retirementAge = 65,
  animated = true,
  className,
}: IncomeValueCalculatorProps) {
  const yearsRemaining = Math.max(0, retirementAge - age);
  const totalValue = monthlyIncome * 12 * yearsRemaining;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className={cn("space-y-6", className)}>
      <div className="text-center">
        <h3 className="font-semibold text-lg">Wert Ihrer Arbeitskraft</h3>
        <p className="text-sm text-muted-foreground">
          Was ist Ihr Einkommen bis zur Pension wert?
        </p>
      </div>

      {/* Calculation Flow */}
      <div className={cn(
        "flex flex-col md:flex-row items-center justify-center gap-4",
        animated && "animate-in fade-in slide-in-from-bottom-4"
      )}>
        {/* Monthly Income */}
        <div className="bg-muted/50 p-4 rounded-lg text-center min-w-[140px]">
          <span className="text-xs text-muted-foreground block">Monatsnetto</span>
          <span className="text-xl font-bold text-primary">{formatCurrency(monthlyIncome)}</span>
        </div>

        <ArrowRight className="h-5 w-5 text-muted-foreground rotate-90 md:rotate-0" />

        {/* Years */}
        <div className="bg-muted/50 p-4 rounded-lg text-center min-w-[140px]">
          <span className="text-xs text-muted-foreground block">× 12 Monate × Jahre</span>
          <span className="text-xl font-bold">{yearsRemaining} Jahre</span>
        </div>

        <ArrowRight className="h-5 w-5 text-muted-foreground rotate-90 md:rotate-0" />

        {/* Total Value */}
        <div className="bg-primary/10 p-4 rounded-lg text-center min-w-[180px] border-2 border-primary/20">
          <span className="text-xs text-primary block">Gesamtwert</span>
          <span className="text-2xl font-bold text-primary">{formatCurrency(totalValue)}</span>
        </div>
      </div>

      {/* Visual Representation */}
      <div className={cn(
        "relative h-32 bg-gradient-to-r from-primary/20 via-primary/40 to-primary rounded-xl overflow-hidden",
        animated && "animate-in fade-in"
      )}
      style={animated ? { animationDelay: '300ms' } : undefined}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <Calculator className="h-10 w-10 mx-auto mb-2 drop-shadow-lg" />
            <p className="font-semibold drop-shadow">
              {formatCurrency(totalValue)}
            </p>
            <p className="text-sm opacity-90">in {yearsRemaining} Jahren Arbeitsleben</p>
          </div>
        </div>
        
        {/* Timeline markers */}
        <div className="absolute bottom-0 left-0 right-0 h-8 flex justify-between px-4 items-center">
          <span className="text-xs text-white/80">Heute ({age} J.)</span>
          <span className="text-xs text-white/80">Pension ({retirementAge} J.)</span>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-lg">
        <p className="text-sm text-center">
          <span className="font-semibold text-amber-600">Wichtig:</span>{" "}
          Dieser Wert ist nur so lange sicher, wie Sie arbeitsfähig bleiben. 
          Eine Berufsunfähigkeitsversicherung schützt diesen Wert.
        </p>
      </div>
    </div>
  );
}
